import React, {useEffect} from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import HomePage from './HomePage';
import BlogPage from './components/Blog/BlogPage';
import BlogPostWrapper from './components/Blog/BlogPostWrapper';
import PrivacyPolicy from './components/PrivacyPolicy';
import UseCases from './components/UseCases/UseCases';
import UseCasePageWrapper from './components/UseCases/UseCasePageWrapper';
import {useScrollDepthTracking} from './components/hooks/useClarity';
import {previewUseCases} from './data/previewUsecases';
import {posts} from './data/posts';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import ProductsPage from "./components/Products/ProductsPage";

const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || 'unknown';

/**
 * Main Application Component with React Router
 */
function App() {

    useEffect(() => {
        if (process.env.NODE_ENV === 'development') return;

        const checkForUpdates = async () => {
            try {
                const response = await fetch('/version.json', { cache: 'no-store' });
                const { version } = await response.json();

                console.log('BUILD_VERSION:', BUILD_VERSION);
                console.log('version.json:', version);
                console.log('Match:', version === BUILD_VERSION);

                if (version !== BUILD_VERSION) {
                    const hasReloaded = sessionStorage.getItem('hasReloaded');
                    console.log('hasReloaded flag:', hasReloaded);
                    if (!hasReloaded) {
                        sessionStorage.setItem('hasReloaded', 'true');
                        window.location.reload();
                    }
                } else {
                    sessionStorage.removeItem('hasReloaded');
                }
            } catch (error) {
                console.log('Version check failed:', error);
            }
        };

        checkForUpdates();
        const interval = setInterval(checkForUpdates, 5 * 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <Router>
            <AppContent />
        </Router>
    );
}

/**
 * Inner component that uses Router context
 */
function AppContent() {
    useScrollDepthTracking();

    return (
        <div className="app">
            <ScrollToTop />
            <Header />

            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                <Route path="/advent-calendar" element={<Navigate to="/use-cases" replace />} />
                {/* Blog post routes (auto-mapped from posts.js) */}
                {posts.filter(p => p.slug && p.component).map(({slug, component: C, t, cat}) => (
                    <Route
                        key={slug}
                        path={`/blog/${slug}`}
                        element={
                            <BlogPostWrapper title={t} category={cat} slug={slug}>
                                <C />
                            </BlogPostWrapper>
                        }
                    />
                ))}
                <Route path="/products" element={<ProductsPage />} />

                {/* Use case routes (auto-mapped from previewUsecases.js) */}
                <Route path="/use-cases" element={<UseCases />} />
                {previewUseCases.map(({p, c: C, d, t, cat, ty}) => (
                    <Route key={p} path={`/use-cases/${p}`} element={
                        <UseCasePageWrapper day={d} title={t} category={cat} type={ty}>
                            <C />
                        </UseCasePageWrapper>
                    } />
                ))}

                <Route path="*" element={<NotFound />} />
            </Routes>

            <Footer />
        </div>
    );
}

const NotFound = () => (
    <div className="container text-center py-5">
        <h1>404 - Page Not Found</h1>
        <p>The page you're looking for doesn't exist.</p>
        <a href="/" className="btn btn-primary">Return Home</a>
    </div>
);

export default App;